/**

 * RAG chat prefill — route contract and prompt templates.

 *

 * Navigation (always starts a NEW chat — never resumes last session):

 *   - Short prompts: `/rag?q=<url-encoded>` + `{ state: { newRagSession: true } }`

 *   - Long prompts:  sessionStorage[`mvkg:prefill-prompt`] + `/rag?prefill=1` + same state

 *

 * RagQuery: when `newRagSession` or `q`/`prefill` params are present, compose mode

 * keeps activeId null and messages empty until the user sends (then POST /sessions).

 * URL stays `/rag` or `/rag?q=…` — not `/rag/<old-session-id>`.

 */



export const PREFILL_STORAGE_KEY = 'mvkg:prefill-prompt';



/** Keep URLs shareable; longer text uses sessionStorage. */

export const MAX_URL_Q_LEN = 480;



export function storeRagPrefillPrompt(prompt) {

  try {

    sessionStorage.setItem(PREFILL_STORAGE_KEY, String(prompt));

  } catch {

    /* quota / private mode */

  }

}



export function readAndClearRagPrefillFromStorage() {

  try {

    const value = sessionStorage.getItem(PREFILL_STORAGE_KEY);

    if (value != null) sessionStorage.removeItem(PREFILL_STORAGE_KEY);

    return value?.trim() || null;

  } catch {

    return null;

  }

}



/**

 * @param {URLSearchParams} searchParams

 * @returns {string|null}

 */

export function consumeRagPrefillFromSearchParams(searchParams) {

  const q = searchParams.get('q');

  if (q?.trim()) return q.trim();

  if (searchParams.get('prefill') === '1') {

    return readAndClearRagPrefillFromStorage();

  }

  return null;

}



export function clearRagPrefillSearchParams(searchParams) {

  const next = new URLSearchParams(searchParams);

  next.delete('q');

  next.delete('prefill');

  return next;

}



/**

 * True when navigation should open compose mode (fresh chat + prefilled input).

 * @param {URLSearchParams} searchParams

 * @param {object|null|undefined} locationState — react-router location.state

 */

export function hasRagPrefillIntent(searchParams, locationState) {

  if (locationState?.newRagSession) return true;

  if (searchParams.get('q')?.trim()) return true;

  if (searchParams.get('prefill') === '1') return true;

  if (searchParams.get('adjudicate') === '1') return true;

  return false;

}



/* ----------------------------------------------------------------------------

 * Claim comparison hand-off → RAG/Ask panel.

 * The comparison tray stores the picked assertions and navigates to

 * /rag?adjudicate=1; RagQuery reads them, calls POST /api/rag/adjudicate, and

 * injects the verdicts as an assistant message (with clickable #id citations).

 * -------------------------------------------------------------------------- */

export const ADJUDICATE_STORAGE_KEY = 'mvkg:adjudicate-items';



export function storeAdjudicateItems(items) {

  try {

    sessionStorage.setItem(ADJUDICATE_STORAGE_KEY, JSON.stringify(items || []));

  } catch {

    /* quota / private mode */

  }

}



export function readAndClearAdjudicateItems() {

  try {

    const v = sessionStorage.getItem(ADJUDICATE_STORAGE_KEY);

    if (v != null) sessionStorage.removeItem(ADJUDICATE_STORAGE_KEY);

    const parsed = v ? JSON.parse(v) : null;

    return Array.isArray(parsed) ? parsed : null;

  } catch {

    return null;

  }

}



export function hasAdjudicateIntent(searchParams) {

  return searchParams.get('adjudicate') === '1';

}



export function navigateToRagWithComparison(navigate, items) {

  storeAdjudicateItems(items || []);

  navigate('/rag?adjudicate=1', { state: { newRagSession: true } });

}



/**

 * @param {import('react-router-dom').NavigateFunction} navigate

 * @param {string} prompt

 */

export function navigateToRagWithPrefill(navigate, prompt) {

  const text = String(prompt || '').trim();

  const state = { newRagSession: true };

  if (!text) {

    navigate('/rag', { state });

    return;

  }

  if (text.length <= MAX_URL_Q_LEN) {

    navigate(`/rag?q=${encodeURIComponent(text)}`, { state });

    return;

  }

  storeRagPrefillPrompt(text);

  navigate('/rag?prefill=1', { state });

}



/** Claim from article digest → full RAG chat. */

export function buildClaimClarifyPrompt({ claim, articleId, articleTitle, verdict }) {

  const title = articleTitle?.trim()

    ? `"${articleTitle.trim()}"`

    : (articleId != null ? `article ${articleId}` : 'this article');

  const verdictNote = verdict && verdict !== 'unverified'

    ? ` (corpus verdict: ${verdict})`

    : '';

  const idNote = articleId != null ? ` (article id ${articleId})` : '';

  return [

    `In the MVKG knowledge graph, clarify and fact-check this claim from ${title}${idNote}${verdictNote}:`,

    '',

    `"${claim}"`,

    '',

    'Investigate before answering — do NOT reply from the claim text alone:',

    `1. Call \`get_entity_details\` on the main method/entity in the claim to pull its graph relations AND the paper's extracted data.`,

    `2. Call \`entity_facts\` on that entity to see what it SOLVES / USES / is EVALUATED_ON / EXTENDS, both directions.`,

    `3. If the graph is thin, ESCALATE in this order: \`related_articles\`${articleId != null ? ` with query="${articleId}"` : ''} (or \`search_articles_content\`) to find corroborating/contradicting papers, then \`read_article_chunk\` with article_id ${articleId != null ? articleId : '<this article>'} to read the source text.`,

    '',

    'Then state plainly: what the claim means, which specific graph evidence or papers support or contradict it, and how it connects to related methods. Address every part. Never answer "only the claim text is available" — if `search_keyword` finds the entity, the data exists; dig with the fact tools.',

  ].join('\n');

}



/** Article digest section (overview, frontier, neighbors, …) → contextual RAG prompt. */

export function buildDigestSectionPrompt({ sectionLabel, articleId, articleTitle, hint }) {

  const title = articleTitle?.trim()

    ? `"${articleTitle.trim()}"`

    : (articleId != null ? `article ${articleId}` : 'this article');

  const idRef = articleId != null ? String(articleId) : '<this article>';

  const lines = [

    `In the MVKG knowledge graph, explain the "${sectionLabel}" section of ${title} (article id ${idRef}).`,

    '',

    'Ground every point in the corpus — do not answer from the section title alone:',

    `1. Call \`get_entity_details\` on the paper's primary method to load its graph relations and extracted data.`,

    `2. Call \`entity_facts\` on that method for its problems, datasets, techniques, and lineage (both directions).`,

    `3. If a section needs more than the graph (overview, frontier, claims), ESCALATE: \`related_articles\` with query="${idRef}" and \`search_articles_content\` for related/neighbor papers, then \`read_article_chunk\` with article_id ${idRef} for the source text.`,

    '',

    'Then explain what matters in this section for a researcher reading the digest: key methods, lineage, claims, and neighbors. Address the section specifically; if the graph is thin, pull the article extraction and related papers rather than saying the data is unavailable.',

  ];

  if (hint?.trim()) {

    lines.splice(1, 0, `Section context: ${hint.trim().slice(0, 400)}${hint.length > 400 ? '…' : ''}`);

  }

  return lines.join('\n');

}



/** PDF reading assistant — user highlighted a passage while reading → RAG. */

export function buildPdfPassagePrompt({ passage, articleId, articleTitle }) {

  const title = articleTitle?.trim()

    ? `"${articleTitle.trim()}"`

    : (articleId != null ? `article ${articleId}` : 'this article');

  const idRef = articleId != null ? String(articleId) : '<this article>';

  const text = String(passage || '').trim().slice(0, 1500);

  return [

    `I'm reading the paper ${title} (article id ${idRef}) and highlighted this passage. Be my reading assistant: clarify what it means and connect it to related work in the corpus.`,

    '',

    `Highlighted passage:`,

    `"${text}"`,

    '',

    'Investigate before answering — do NOT explain from the passage text alone:',

    `1. FIRST search the corpus for the methods/claims/entities this passage is about: \`search_articles_content\` on the key terms, and \`get_entity_details\` + \`entity_facts\` on the main method/concept for its graph relations (what it SOLVES / USES / is EVALUATED_ON / EXTENDS, both directions).`,

    `2. Use \`related_articles\` with query="${idRef}" and \`methods_for\` to find similar methods and neighbouring papers in the graph.`,

    `3. If the corpus has nothing relevant, fall back to reading the source paper itself: \`read_article_chunk\` with article_id ${idRef} to ground your answer in the surrounding text.`,

    '',

    'Then, as a reading assistant: explain the concept in plain terms in the context of this paper, point to the related methods/claims you found in the graph, and cite which tool results or papers you used. Never answer "only the passage is available" — if the terms appear in the corpus, dig with the fact tools; otherwise read the article.',

  ].join('\n');

}



/** Discovery / dashboard method row → summarize method in corpus context. */

export function buildDiscoveryMethodPrompt({ label, methodId, disruption, novelty }) {

  const name = label || methodId;

  const scores = [

    typeof disruption === 'number' ? `disruption ${disruption.toFixed(2)}` : null,

    typeof novelty === 'number' ? `novelty ${novelty.toFixed(2)}` : null,

  ].filter(Boolean).join(', ');

  return [

    `Summarize the method "${name}" in the MVKG corpus${scores ? ` (${scores})` : ''}.`,

    '',

    `First call \`get_entity_details\` on "${name}" for its graph relations and extracted data, then \`entity_facts\` on "${name}" for problems it SOLVES, datasets it is EVALUATED_ON, techniques it USES, and what it EXTENDS (lineage, both directions). Use \`methods_for\` to find sibling methods on the same problems, and \`related_articles\` with query="${name}" to broaden if the graph is thin.`,

    '',

    'Then cover: genealogy (what it builds on and what builds on it), the key problems it targets, the most related methods, and why it ranks where it does on the Discovery disruption/novelty metrics. Ground each point in the tool output; do not answer from the method name alone.',

  ].join('\n');

}



/** Discovery temporal-trend row (growing / declining / stable) → RAG prompt. */

export function buildTemporalTrendPrompt({ label, methodId, trend, year, fromYear, toYear }) {

  const name = label || methodId;

  const windowNote = fromYear && toYear ? ` over the ${fromYear}–${toYear} window` : '';

  const yearNote = typeof year === 'number' ? ` (appears around ${year})` : '';

  const ask = {

    growing:

      `Why is "${name}" a GROWING / rising direction in this corpus${windowNote}? What's driving its momentum, and what does it build on?`,

    declining:

      `Why is "${name}" DECLINING in this corpus${windowNote}? What newer methods are superseding it, and why?`,

    stable:

      `"${name}" looks STABLE in this corpus${windowNote} — established but not surging. What role does it play, and what keeps it steady rather than rising or fading?`,

  }[trend] || `Explain the temporal trend of "${name}" in this corpus${windowNote}.`;

  return [

    `${ask}${yearNote}`,

    '',

    `First call \`get_entity_details\` on "${name}" for its graph relations and extracted data, then \`entity_facts\` on "${name}" for the problems it SOLVES, datasets it is EVALUATED_ON, techniques it USES, and what it EXTENDS (lineage, both directions). Use \`methods_for\` to find sibling methods on the same problems, and \`related_articles\` with query="${name}" to see neighbouring papers and how the surrounding research is shifting.`,

    '',

    `Then explain the trend grounded in the corpus: ${trend === 'declining' ? 'what is replacing it and why interest is moving away' : trend === 'stable' ? 'why it persists as a steady building block' : 'what is driving the rise and which recent work builds on it'}, its lineage, the key problems it targets, and the most related methods. Ground every point in the tool output; do not answer from the method name alone.`,

  ].join('\n');

}



/**

 * Discovery temporal-trends PANEL → an OVERALL "big picture" RAG prompt.

 * Hands RAG the live counts + the top growing/declining method names currently

 * on screen, and asks for the overall research-shift narrative (grounded).

 */

export function buildTemporalTrendsOverviewPrompt({

  growing,

  declining,

  stable,

  topGrowing = [],

  topDeclining = [],

  fromYear,

  toYear,

  collection,

}) {

  const scope = collection && collection !== 'all'

    ? `the "${collection}" collection`

    : 'this corpus';

  const window = fromYear && toYear ? `, ${fromYear}–${toYear}` : '';

  const counts = [

    typeof growing === 'number' ? `${growing} methods are rising` : null,

    typeof declining === 'number' ? `${declining} are declining` : null,

    typeof stable === 'number' ? `${stable} are holding steady` : null,

  ].filter(Boolean).join(', ');

  const gList = topGrowing.filter(Boolean).slice(0, 8).map((n) => `"${n}"`).join(', ');

  const dList = topDeclining.filter(Boolean).slice(0, 8).map((n) => `"${n}"`).join(', ');

  const lines = [

    `Give me the big picture of the research trends in ${scope}${window}.${counts ? ` Right now ${counts}.` : ''}`,

  ];

  if (gList) lines.push('', `The fastest-rising methods include: ${gList}.`);

  if (dList) lines.push('', `The fading methods include: ${dList}.`);

  lines.push(

    '',

    `Investigate before answering — do NOT speculate from the names alone. For the most important rising and fading methods above, call \`get_entity_details\` and \`entity_facts\` to see what they SOLVE / USE / are EVALUATED_ON / EXTEND (lineage, both directions), use \`methods_for\` to find sibling methods on the same problems, and \`related_articles\` to see how the surrounding work is shifting. Look for the common threads across the rising set and across the fading set.`,

    '',

    'Then tell me the big picture: what overall research shift does this reflect, which directions/paradigms are gaining vs losing momentum and WHY, how the rising methods relate to (or supersede) the fading ones, and what I should pay attention to next. Ground every point in the tool output and name specific methods/papers.',

  );

  return lines.join('\n');

}



/** Author profile → RAG prompt. */

export function buildAuthorInsightsPrompt({ authorName, authorId }) {

  const label = authorName || authorId || 'this author';

  return [

    `Profile the author "${label}" using the MVKG corpus.`,

    '',

    `Investigate before answering — there is no direct author→claim edge, so use the dedicated author tools:`,

    `1. Call \`author_works\` with author_name="${label}" to list their papers and the primary methods of each.`,

    `2. Call \`author_claims\` with author_name="${label}" to pull the claims made across those papers.`,

    `3. For the 2-3 most important methods, call \`entity_facts\` (and \`get_entity_details\` if needed) to map problems, datasets, lineage, and collaborators.`,

    '',

    'Then cover: their main research focus and methods, notable claims and contributions, cross-domain influence (which problems/datasets their work spans), frequent collaborators, and comparable authors. Address every part. If a tool returns little, try a name variant or call `related_articles` to pull neighbouring papers — never answer "only the author name is available".',

  ].join('\n');

}



/** Graph node selection → summarize node + neighborhood. */

export function buildGraphNodePrompt({

  nodeName,

  nodeId,

  nodeType,

  view,

  year,

  description,

  userQuestion,

}) {

  if (userQuestion?.trim()) return userQuestion.trim();

  const label = nodeName || nodeId || 'this node';

  const meta = [nodeType, view && `${view} view`, year].filter(Boolean).join(' · ');

  const desc = description?.trim();

  const lines = [

    `Explain the MVKG graph node "${label}"${meta ? ` (${meta})` : ''} and its neighborhood.`,

    '',

    `Investigate first: call \`get_entity_details\` on "${label}" for its graph relations and extracted paper data, then \`entity_facts\` on "${label}" for problems/datasets/techniques/lineage (both directions). Use \`subgraph_for\` to see its immediate neighbors. If the data looks thin, ESCALATE: \`related_articles\` with query="${label}" then \`read_article_chunk\` for the source papers.`,

    '',

    'Then cover: key relationships, lineage (what it builds on and what builds on it), the problems/tasks it targets, the authors behind it, and why it matters in the corpus. Address every part; if `search_keyword` found this node the data EXISTS — pull the article extraction and related papers rather than answering "only the node name is available".',

  ];

  if (desc) lines.splice(1, 0, `Node context: ${desc.slice(0, 280)}${desc.length > 280 ? '…' : ''}`);

  return lines.join('\n');

}



/** Graph cluster selection -> summarize a research direction / star system. */

export function buildGraphClusterPrompt({
  clusterName,
  clusterId,
  topMethods = [],
  view,
  collectionId,
  userQuestion,
}) {
  if (userQuestion?.trim()) return userQuestion.trim();

  const label = clusterName || (clusterId != null ? `Cluster ${clusterId}` : 'this research cluster');
  const methodList = Array.isArray(topMethods) ? topMethods.filter(Boolean).slice(0, 10) : [];
  const meta = [
    view && `${view} view`,
    collectionId && collectionId !== 'all' ? `collection ${collectionId}` : null,
  ].filter(Boolean).join(' · ');

  const lines = [
    `Explain the MVKG research cluster "${label}"${meta ? ` (${meta})` : ''}.`,
    '',
    'Investigate first: use the graph tools to inspect the cluster members, representative methods, shared problems, datasets, techniques, lineage, and links to neighbouring clusters.',
    '',
    'Then cover: what this cluster is about, which methods lead it, what problems it is trying to solve, how the cluster is evolving, and what a researcher should inspect next. Ground every point in corpus evidence rather than the cluster label alone.',
  ];

  if (methodList.length > 0) {
    lines.splice(1, 0, `Representative methods: ${methodList.map((m) => `"${m}"`).join(', ')}.`);
  }

  return lines.join('\n');
}



/** Graph edge selection → explain relationship. */

export function buildGraphEdgePrompt({

  sourceName,

  targetName,

  sourceId,

  targetId,

  relation,

  view,

  userQuestion,

}) {

  if (userQuestion?.trim()) return userQuestion.trim();

  const s = sourceName || sourceId || 'source';

  const t = targetName || targetId || 'target';

  const rel = (relation || 'related').replace(/_/g, ' ').toLowerCase();

  return [

    `In the MVKG ${view || 'fusion'} view, explain the "${rel}" relationship between "${s}" and "${t}".`,

    '',

    `Investigate first: call \`entity_facts\` on "${s}" and on "${t}" to see their shared problems/datasets/techniques and lineage, then \`compare_methods\` with method_names="${s}, ${t}" for what they share vs what is unique to each. Use \`related_articles\` or \`search_articles_content\` to find the papers that link them.`,

    '',

    'Then answer: why are they linked in the graph, which specific papers support the link, and how does this fit the broader method genealogy (does one extend or build on the other)? Address every part; if the graph is thin, pull the supporting papers rather than answering "only the node names are available".',

  ].join('\n');

}


